import { z } from 'zod';
import { type TracePhase } from './trace-tool-filters';
import { type TraceToolCall } from '../types';

const READ_FILE_PATH_KEYS = [
  'target_file',
  'targetFile',
  'file_path',
  'filePath',
  'path',
] as const;

const ReadFilePathArgsSchema = z.object({
  target_file: z.string().optional(),
  targetFile: z.string().optional(),
  file_path: z.string().optional(),
  filePath: z.string().optional(),
  path: z.string().optional(),
});

const ReadFileStructuredOutputSchema = z.object({
  filePath: z.string().optional(),
  content: z.string().optional(),
  contentLength: z.number().optional(),
});

export type TraceReadFileEntry = {
  id: string;
  filePath?: string;
  fileName?: string;
  displayName: string;
  output: string;
  error?: string;
  isMarkdown: boolean;
};

const LINE_NUMBER_PREFIX_PATTERN = /^ {0,6}\d+[\t|]/;
const PARTIAL_VIEW_FOOTER_PATTERN = /^\[PARTIAL view:/;

export function stripReadFileLineNumbers(content: string): string {
  return content
    .split('\n')
    .map((line) =>
      LINE_NUMBER_PREFIX_PATTERN.test(line)
        ? line.replace(LINE_NUMBER_PREFIX_PATTERN, '')
        : line,
    )
    .join('\n');
}

export function isNumberedReadFileView(content: string): boolean {
  const bodyLines = content
    .split('\n')
    .filter(
      (line) => line.length > 0 && !PARTIAL_VIEW_FOOTER_PATTERN.test(line),
    );

  return (
    bodyLines.length > 0 &&
    bodyLines.every((line) => LINE_NUMBER_PREFIX_PATTERN.test(line))
  );
}

export function extractReadFilePath(args: unknown): string | undefined {
  const parsed = ReadFilePathArgsSchema.safeParse(args);

  if (!parsed.success) {
    return undefined;
  }

  for (const key of READ_FILE_PATH_KEYS) {
    const value = parsed.data[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
}

function extractReadFilePathFromOutput(output: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(output);
    const structured = ReadFileStructuredOutputSchema.safeParse(parsed);

    if (structured.success && structured.data.filePath?.trim()) {
      return structured.data.filePath.trim();
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function resolveReadFileRawOutput(output: string): string {
  try {
    const parsed: unknown = JSON.parse(output);
    const structured = ReadFileStructuredOutputSchema.safeParse(parsed);

    if (structured.success && typeof structured.data.content === 'string') {
      return structured.data.content;
    }
  } catch {
    return output;
  }

  return output;
}

export function isSkillFilePath(filePath?: string): boolean {
  if (!filePath) {
    return false;
  }

  const normalizedPath = filePath.replace(/\\/g, '/');

  if (/\/skill\.md$/i.test(normalizedPath)) {
    return true;
  }

  return (
    (normalizedPath.includes('/skills/') ||
      normalizedPath.includes('/.subagent-skills/')) &&
    /\/SKILL\.md$/i.test(normalizedPath)
  );
}

function resolveReadFilePathFromToolData({
  input,
  output,
}: {
  input?: unknown;
  output?: unknown;
}): string | undefined {
  const pathFromInput = extractReadFilePath(input);

  if (pathFromInput) {
    return pathFromInput;
  }

  if (typeof output === 'string') {
    return extractReadFilePathFromOutput(output);
  }

  if (output !== undefined && output !== null) {
    try {
      return extractReadFilePathFromOutput(JSON.stringify(output));
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export function shouldIncludeCoPilotReadFileToolCall({
  input,
  output,
}: {
  input?: unknown;
  output?: unknown;
}): boolean {
  return isSkillFilePath(resolveReadFilePathFromToolData({ input, output }));
}

export function getReadFileName(filePath?: string): string | undefined {
  if (!filePath) {
    return undefined;
  }

  const fileName = filePath.split('/').pop();

  return fileName && fileName.length > 0 ? fileName : undefined;
}

export function getReadFileDisplayName(filePath?: string): string {
  const fileName = getReadFileName(filePath);
  const lowerFileName = fileName?.toLowerCase() ?? '';

  if (
    lowerFileName.startsWith('code_result') &&
    lowerFileName.endsWith('.txt')
  ) {
    return 'python script result';
  }

  if (lowerFileName.startsWith('code') && lowerFileName.endsWith('.py')) {
    return 'python script';
  }

  return 'file';
}

export function formatTruncatedFilePath({
  filePath,
  maxLength = 72,
}: {
  filePath: string;
  maxLength?: number;
}): string {
  if (filePath.length <= maxLength) {
    return filePath;
  }

  const separatorIndex = Math.max(
    filePath.lastIndexOf('/'),
    filePath.lastIndexOf('\\'),
  );
  const fileName =
    separatorIndex >= 0 ? filePath.slice(separatorIndex + 1) : filePath;
  const directory =
    separatorIndex >= 0 ? filePath.slice(0, separatorIndex + 1) : '';

  const reservedForFileName = fileName.length + 3;

  if (reservedForFileName >= maxLength) {
    return `...${fileName.slice(-(maxLength - 3))}`;
  }

  const directoryBudget = maxLength - reservedForFileName;
  const directoryPrefix = directory.slice(0, Math.max(0, directoryBudget));

  return `${directoryPrefix}...${fileName}`;
}

export function getReadFileEditorLanguage(
  filePath?: string,
): 'python' | 'javascript' | 'json' | 'html' | 'text' {
  const lowerPath = filePath?.toLowerCase() ?? '';

  if (lowerPath.endsWith('.py')) {
    return 'python';
  }

  if (lowerPath.endsWith('.html') || lowerPath.endsWith('.htm')) {
    return 'html';
  }

  if (
    lowerPath.endsWith('.ts') ||
    lowerPath.endsWith('.tsx') ||
    lowerPath.endsWith('.js') ||
    lowerPath.endsWith('.jsx')
  ) {
    return 'javascript';
  }

  if (lowerPath.endsWith('.json')) {
    return 'json';
  }

  return 'text';
}

const READ_FILE_MARKDOWN_EXTENSIONS = new Set(['.md', '.markdown', '.mdx']);

const READ_FILE_KNOWN_CODE_EXTENSIONS = new Set([
  '.py',
  '.ts',
  '.tsx',
  '.js',
  '.jsx',
  '.json',
  '.txt',
  '.yaml',
  '.yml',
  '.sh',
  '.bash',
  '.go',
  '.rs',
  '.java',
  '.rb',
  '.php',
  '.cs',
  '.cpp',
  '.c',
  '.h',
  '.sql',
  '.xml',
  '.html',
  '.css',
  '.scss',
  '.vue',
  '.toml',
  '.ini',
  '.env',
]);

function getReadFileExtension(filePath?: string): string | undefined {
  const fileName = getReadFileName(filePath)?.toLowerCase();

  if (!fileName) {
    return undefined;
  }

  const dotIndex = fileName.lastIndexOf('.');

  if (dotIndex <= 0) {
    return undefined;
  }

  return fileName.slice(dotIndex);
}

export function isMarkdownFilePath(filePath?: string): boolean {
  const extension = getReadFileExtension(filePath);

  return (
    extension !== undefined && READ_FILE_MARKDOWN_EXTENSIONS.has(extension)
  );
}

function isCoPilotMarkdownPath(filePath?: string): boolean {
  if (!filePath) {
    return false;
  }

  return (
    filePath.startsWith('/artifacts/') || filePath.startsWith('/workspace/')
  );
}

function resolveReadFileIsMarkdown({
  filePath,
  output,
  tracePhase,
}: {
  filePath?: string;
  output: string;
  tracePhase?: TracePhase;
}): boolean {
  const extension = getReadFileExtension(filePath);

  if (isMarkdownFilePath(filePath)) {
    return true;
  }

  if (extension && READ_FILE_KNOWN_CODE_EXTENSIONS.has(extension)) {
    return false;
  }

  if (tracePhase === 'co-pilot' && isCoPilotMarkdownPath(filePath)) {
    return readFileOutputLooksLikeMarkdown(output);
  }

  return readFileOutputLooksLikeMarkdown(output);
}

export function readFileOutputLooksLikeMarkdown(content: string): boolean {
  return (
    content.includes('##') ||
    content.includes('**') ||
    /^[-*]\s/m.test(content) ||
    /^\d+\.\s/m.test(content) ||
    content.includes('```')
  );
}

export function toTraceReadFileEntry(
  toolCall: TraceToolCall,
  options?: { tracePhase?: TracePhase },
): TraceReadFileEntry {
  const tracePhase = options?.tracePhase;
  const rawOutput = toolCall.output?.trim() ?? '';
  const resolvedOutput = resolveReadFileRawOutput(rawOutput);
  const filePath =
    extractReadFilePath(toolCall.args) ??
    extractReadFilePathFromOutput(rawOutput);
  const output =
    tracePhase === 'co-pilot' || isNumberedReadFileView(resolvedOutput)
      ? stripReadFileLineNumbers(resolvedOutput).trim()
      : resolvedOutput.trim();
  const displayName =
    tracePhase === 'co-pilot'
      ? (getReadFileName(filePath) ?? 'file')
      : getReadFileDisplayName(filePath);

  return {
    id: toolCall.id,
    filePath,
    fileName: getReadFileName(filePath),
    displayName,
    output,
    error: toolCall.error,
    isMarkdown: resolveReadFileIsMarkdown({ filePath, output, tracePhase }),
  };
}
